import {
    APPROVAL_STATUS_TITLES,
    type ApprovalResponseStatus,
    parseLawyerApprovalAction,
} from '@common/enums';
import type { ApprovalResponse } from '@common/types';
import { Box, Button, TextField, Typography } from '@mui/material';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getLawyerApprovalStatus } from '@/services/EmailApprovalService';

const commonContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '8px',
};

const commonButtonStyles = {
    width: 'max-content',
    textTransform: 'none',
    padding: '8px 20px',
};

interface LawyerQuoteApprovalProps {
    title: string;
    message: string;
}

function LawyerQuoteApproval({ title, message }: LawyerQuoteApprovalProps) {
    return (
        <Box sx={commonContainerStyles}>
            <Typography variant="h3">{title}</Typography>
            <Typography variant="body2" color="text.primary">
                {message}
            </Typography>
        </Box>
    );
}

interface Props {
    emailResponseAction: string;
    status: string;
    failedMessage?: string;
}

export default function LawyerResponseContent({
    emailResponseAction,
    status,
    failedMessage,
}: Props) {
    const [isDeclined, setIsDeclined] = useState<boolean>(false);
    const [errorInApiResponse, setErrorInApiResponse] = useState<ApprovalResponse | null>(null);
    const [declineReason, setDeclineReason] = useState<string>('');

    const [searchParams] = useSearchParams();
    const { instanceId, prospectId } = useParams();
    const action = searchParams.get('Action');

    const handleDecline = async () => {
        if (!instanceId || !prospectId) return;
        try {
            await getLawyerApprovalStatus(
                instanceId,
                prospectId,
                parseLawyerApprovalAction(action),
                { reason: declineReason },
            );
            setIsDeclined(true);
        } catch (error: unknown) {
            console.error(error);
            if (isAxiosError(error)) {
                setErrorInApiResponse(error?.response?.data);
            }
        }
    };

    const declinedReason = (
        <Box sx={commonContainerStyles}>
            <Typography variant="h1">Decline reason</Typography>
            <Typography variant="body2" color="text.primary">
                Describe the decline reason to be sent to the client.
            </Typography>
            <TextField
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="e.g. “I have changed my plans”; “I don't like the pricing” etc."
            />
            <Box sx={{ display: 'flex', gap: '10px' }}>
                <Button variant="outlined" sx={commonButtonStyles}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleDecline} sx={commonButtonStyles}>
                    Confirm
                </Button>
            </Box>
        </Box>
    );
    const apiErrorResponse = (
        <Box sx={commonContainerStyles}>
            <Typography variant="h1">
                {APPROVAL_STATUS_TITLES[errorInApiResponse?.Status as ApprovalResponseStatus]}
            </Typography>
            <Typography variant="body2" color="text.primary">
                {errorInApiResponse?.Message}
            </Typography>
        </Box>
    );
    const quoteDeclined = (
        <Box sx={commonContainerStyles}>
            <Typography variant="h1">Quote declined</Typography>
            <Typography variant="body2" color="text.primary">
                You have successfully declined the quote.
            </Typography>
        </Box>
    );
    const handleActionRender = (status: string, emailResponseAction: string) => {
        switch (status) {
            case 'Success':
                if (emailResponseAction === 'Approved') {
                    return (
                        <LawyerQuoteApproval
                            title="You approved the quote"
                            message="The quote has been sent to the client."
                        />
                    );
                } else if (emailResponseAction === 'Declined') {
                    return isDeclined
                        ? quoteDeclined
                        : errorInApiResponse
                          ? apiErrorResponse
                          : declinedReason;
                } else {
                    return (
                        <LawyerQuoteApproval
                            title="Invalid Action"
                            message="Invalid Action Received, allowed actions are a. Approve  b. Decline"
                        />
                    );
                }
            case 'LawyerAlreadyApproved':
                return (
                    <LawyerQuoteApproval
                        title="Action Already Taken"
                        message={failedMessage || ''}
                    />
                );
            case 'LawyerAlreadyRejected':
                return (
                    <LawyerQuoteApproval
                        title="Action Already Taken"
                        message={failedMessage || ''}
                    />
                );
            case 'ClientAlreadyAccepted':
                return (
                    <LawyerQuoteApproval
                        title="Action Already Taken"
                        message={failedMessage || ''}
                    />
                );
            case 'ClientAlreadyDeclined':
                return (
                    <LawyerQuoteApproval
                        title="Action Already Taken"
                        message={failedMessage || ''}
                    />
                );
            default:
                return (
                    <LawyerQuoteApproval
                        title={
                            APPROVAL_STATUS_TITLES[status as ApprovalResponseStatus]
                                ? APPROVAL_STATUS_TITLES[status as ApprovalResponseStatus]
                                : 'Something went wrong!'
                        }
                        message={failedMessage || ''}
                    />
                );
        }
    };

    return handleActionRender(status, emailResponseAction);
}
